import React, { useState } from "react";
import { HashLink } from "react-router-hash-link";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LanguageSelector } from "./LanguageSelector";
import instaIcon from "../assets/svg/instagram.svg";
import facebookIcon from "../assets/svg/facebook.svg";
import linkedInIcon from "../assets/svg/linkedIn.svg";
import discordIcon from "../assets/svg/discord.svg";
import tiktokIcon from "../assets/images/icons/tiktok.png";
import youtubeIcon from "../assets/images/icons/youtube.png";
import redditIcon from "../assets/images/icons/reddit.png";
import LiiferLogo from "../assets/images/liifer-logo-no-tm.png";
import whiteArrowSvg from "../assets/svg/pointing-arrow.svg";
import blueArrowSvg from "../assets/svg/pointing-blue-arrow.svg";
import { useAppSelector } from "../store/hooks";
import { CommentForm } from "./CommentForm";
import { Modal } from "./dashboard/Modal";
import { ResponseOption } from "./ResponseOption";
import { SuccessToast } from "./dashboard/SuccessToast";

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const navigationIsDisabled = useAppSelector(
    (state) => state.meals.navigationIsDisabled
  );
  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const { accessToken } = useAppSelector((state: any) => state.auth);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showResponseOption, setShowResponseOption] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const { pathname } = useLocation();
  const onCheckOutPage = pathname.slice(0, 19) === "/dashboard/checkout";

  const onCompletedModal = () => {
    setShowToast(true);
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false);
    setShowResponseOption(false);
  };

  const showCommentForm = () => {
    setShowResponseOption(false);
  };

  const showResponseComponent = () => {
    setShowResponseOption(true);
  };

  const scrollTopHomePage = () => {
    if (window.location.pathname === "/home") {
      // If already on /home, just scroll to top
      const targetComponent = document.getElementById("top-landing");
      if (targetComponent) {
        targetComponent.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <>
      <footer
        className={`${!accessToken ? "pl-0" : "md:pl-12"}
        z-20 footer--pin bg-[#111218] text-slate-200 overflow-x-hidden pt-10 `}
      >
        <div className="w-11/12 mx-auto">
          <div className="text-xl sm:text-base sm:h-72 md:h-64">
            <div className="flex justify-center lg:justify-start">
              <Link
                className="hover:opacity-70 transition duration-300 ease-in-out"
                to="/home"
                draggable={false}
              >
                <button type="button" onClick={scrollTopHomePage}>
                  <img className="h-10" src={LiiferLogo} alt="Liifer Logo" />
                </button>
              </Link>
            </div>
            <div className="xl:flex xl:justify-between xl:items-start pt-10">
              <div className="flex flex-col sm:flex-row justify-between lg:justify-start lg:space-x-28">
                <div className="text-center sm:text-left flex flex-col sm:pr-3 lg:pr-0">
                  <div className="text-blue-500 pb-2">
                    {t("footer.product")}
                  </div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://www.overwolf.com/app/Liifer_Technologies_Inc-Liifer_Application"
                      draggable={false}
                    >
                      {t("footer.liiferOverwolfApp")}
                    </a>
                  </div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://apps.apple.com/app/liifer/id6468917016"
                      draggable={false}
                    >
                      {t("footer.liiferIOSApp")}
                    </a>
                  </div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href="https://play.google.com/store/apps/details?id=com.liifer.app&hl"
                      draggable={false}
                    >
                      {t("footer.liiferAndroidApp")}
                    </a>
                  </div>
                </div>
                <div className="text-center sm:text-left flex flex-col sm:px-3 lg:px-0">
                  <div className="text-blue-500 pb-2">
                    {t("footer.company")}
                  </div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <HashLink
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      }`}
                      to="/home/#aboutUs"
                      draggable={false}
                    >
                      {t("footer.aboutUs")}
                    </HashLink>
                  </div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <Link
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      }`}
                      to="/partners"
                      draggable={false}
                    >
                      {t("footer.parterWithUs")}
                    </Link>
                  </div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <Link
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      }`}
                      to="/streamers"
                      draggable={false}
                    >
                      {t("footer.streamers")}
                    </Link>
                  </div>
                  {!userInfo && (
                    <div className="pb-4 sm:pb-1 hover:text-slate-300">
                      <a
                        className={`${
                          navigationIsDisabled ? "pointer-events-none" : ""
                        } hover:underline`}
                        draggable={false}
                        href="/locations/en/food/delivery/food_delivery.htm"
                      >
                        {t("footer.locations")}
                      </a>
                    </div>
                  )}
                </div>
                <div className="text-center sm:text-left flex flex-col sm:px-3 lg:px-0">
                  <div className="text-blue-500 pb-2">{t("footer.legal")}</div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-200">
                    <Link
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      }`}
                      to="/terms"
                      draggable={false}
                    >
                      {t("termsOfService")}
                    </Link>
                  </div>

                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <Link
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      }`}
                      to="/privacy"
                      draggable={false}
                    >
                      {t("privacyPolicy")}
                    </Link>
                  </div>

                  <div className="pb-4 sm:pb-1 hover:text-slate-300">
                    <Link
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      } hover:underline`}
                      to="/FAQ/whatIsLiifer/#responsibilities"
                      draggable={false}
                    >
                      {t("FAQ.liifersResponsibilities")}
                    </Link>
                  </div>
                </div>
                <div className="text-center sm:text-left flex flex-col sm:pl-3 lg:pl-0">
                  <div className="text-blue-500 pb-2">
                    {t("footer.support")}
                  </div>
                  <div className="pb-4 sm:pb-1 hover:text-slate-300">
                    <Link
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      } hover:underline`}
                      to="/FAQ"
                      draggable={false}
                    >
                      {t("footer.FAQ")}
                    </Link>
                  </div>
                  <div className="pb-4 sm:pb-1 hover:underline hover:text-slate-300">
                    <Link
                      className={`${
                        navigationIsDisabled ? "pointer-events-none" : ""
                      }`}
                      to="/contact"
                      draggable={false}
                    >
                      {t("footer.contactUs")}
                    </Link>
                  </div>
                </div>
              </div>
              <div className="space-y-4 hidden xl:block pl-4">
                <a
                  className="group border-2 border-slate-200 hover:border-blue-400 hover:text-blue-400 rounded-xl px-4 h-14 flex justify-between items-center space-x-2 w-64 transition ease-in-out duration-200"
                  href="https://discord.gg/npWGrtvua6"
                  target="_blank"
                  rel="noreferrer"
                >
                  <p className="text-left">{t("footer.askQuestionDiscord")}</p>
                  <img
                    className="h-3 group-hover:hidden"
                    src={whiteArrowSvg}
                    alt="Arrow"
                  />
                  <img
                    className="h-3 hidden group-hover:block"
                    src={blueArrowSvg}
                    alt="Arrow"
                  />
                </a>

                <button
                  className="group border-2 border-slate-200 rounded-xl px-4 h-14 flex justify-between items-center space-x-2 w-64 hover:border-blue-400 hover:text-blue-400 transition ease-in-out duration-200"
                  onClick={() => setShowModal(true)}
                >
                  <p className="text-left">{t("footer.askQuestionEmail")}</p>
                  <img
                    className="h-3 group-hover:hidden"
                    src={whiteArrowSvg}
                    alt="Arrow"
                  />
                  <img
                    className="h-3 hidden group-hover:block"
                    src={blueArrowSvg}
                    alt="Arrow"
                  />
                </button>
              </div>
            </div>
          </div>
          <div className="space-y-4 sm:space-y-0 sm:flex sm:justify-between items-center pb-4 border-b border-slate-600">
            <div className="flex justify-center sm:justify-start space-x-2">
              <a
                className="hover:opacity-80"
                href={"https://discord.gg/npWGrtvua6"}
                draggable={false}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="h-10 sm:h-5 cursor-pointer"
                  src={discordIcon}
                  alt="discord icon"
                />
              </a>
              <a
                className="hover:opacity-80"
                href={"https://www.reddit.com/r/Liifer/"}
                draggable={false}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="h-10 sm:h-5 cursor-pointer"
                  src={redditIcon}
                  alt="reddit icon"
                />
              </a>
              <a
                className="hover:opacity-80"
                href={"https://www.youtube.com/@LiiferOfficial"}
                draggable={false}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="h-10 sm:h-5 cursor-pointer"
                  src={youtubeIcon}
                  alt="youtube icon"
                />
              </a>
              <a
                className="hover:opacity-80"
                href={"https://instagram.com/liifertech?utm_medium=copy_link"}
                draggable={false}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="h-10 sm:h-5 cursor-pointer"
                  src={instaIcon}
                  alt="instagram icon"
                />
              </a>
              <a
                className="hover:opacity-80"
                href={
                  "https://www.facebook.com/Liifer-127004712897996/?view_public_for=127004712897996"
                }
                draggable={false}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="h-10 sm:h-5 cursor-pointer"
                  src={facebookIcon}
                  alt="facebook icon"
                />
              </a>
              <a
                className="hover:opacity-80"
                href={"https://www.tiktok.com/@liifer.com"}
                draggable={false}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="h-10 sm:h-5 cursor-pointer"
                  src={tiktokIcon}
                  alt="tiktok icon"
                />
              </a>
              <a
                className="hover:opacity-80"
                href={"https://www.linkedin.com/company/liifer/"}
                draggable={false}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  className="h-10 sm:h-5 cursor-pointer"
                  src={linkedInIcon}
                  alt="linkedIn icon"
                />
              </a>
            </div>
            <div
              className={`${
                onCheckOutPage ? "hidden" : ""
              } mx-auto sm:mx-0 w-12 lg:w-11 pl-1.5 sm:pl-0`}
            >
              <LanguageSelector />
            </div>
          </div>

          <div className="py-6 text-xs text-center lg:text-left">
            <h1 className=" text-slate-400 font-thin">
              {t("footer.trademark")}
            </h1>
            <div className="flex flex-col lg:flex-row justify-between">
              <div className="py-1 text-slate-400 text-xs font-thin">
                {t("footer.brandCopyRight")}
              </div>
              <span className=" text-slate-400 font-thin">
                {t("footer.copyright")} © 2025{" "}
                <span className="text-blue-500">liifer.</span>{" "}
                {t("footer.allRightsReserved")}
              </span>
            </div>
          </div>
        </div>
      </footer>

      {showModal && (
        <Modal
          show={showModal}
          handleClose={closeModal}
          title={t("comment.title")}
        >
          {showResponseOption ? (
            <ResponseOption
              setShowResponseOptionModal={setShowModal}
              onShowCommentForm={showCommentForm}
            />
          ) : (
            <CommentForm
              onCompleted={onCompletedModal}
              setShowCommentModal={setShowModal}
              onshowResponseComponent={showResponseComponent}
            />
          )}
        </Modal>
      )}

      <SuccessToast
        show={showToast}
        handleClose={() => setShowToast(false)}
        message={
          <span className="text-slate-100 text-base space-x-2 flex font-bold">
            {t("comment.success")}
          </span>
        }
      />
    </>
  );
};

export default Footer;
